import { useState, useEffect } from "react";
import { Button, Container, Stack, Typography, TextField, Paper, Alert, Divider } from "@mui/material";
import Page from "src/components/Page";
import FormItem from "src/components/form-item/FormItem";
import { asyncRequest } from "src/services/network";
import { alert } from "src/services/alert";

/**
 * 支付业务配置页面
 * 包含线下支付分红配置和线上秒杀区入池留存配置
 */
export default function PaymentConfig() {
  const [offline, setOffline] = useState({
    merchantDividendRatio: 0.25,
    consumerDividendRatio: 0.25,
    consumerReferrerDividendRatio: 0.10,
    merchantReferrerDividendRatio: 0.10,
    companyRetentionRatio: 0.15,
    otherRolesEquityRatio: 0.10,
    companyOpsEquityRatio: 0.02,
    companyOpsUid: "",
    companyMarketEquityRatio: 0.02,
    companyMarketUid: "",
    companySchoolEquityRatio: 0.01,
    companySchoolUid: "",
  });

  const [online, setOnline] = useState({
    poolRetentionRatio: 0,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  async function fetchConfig() {
    try {
      const { data } = await asyncRequest({
        method: "GET",
        path: "/system/config/payment-config/query",
      });
      if (data) {
        if (data.offline) setOffline((prev) => ({ ...prev, ...data.offline }));
        if (data.online) setOnline((prev) => ({ ...prev, ...data.online }));
      }
    } catch (e) {
      console.error("获取支付分红配置失败:", e);
    }
  }

  // 线下比例之和校验
  const offlineRatioKeys = [
    "merchantDividendRatio",
    "consumerDividendRatio",
    "consumerReferrerDividendRatio",
    "merchantReferrerDividendRatio",
    "companyRetentionRatio",
    "otherRolesEquityRatio",
    "companyOpsEquityRatio",
    "companyMarketEquityRatio",
    "companySchoolEquityRatio",
  ];
  const offlineTotal = offlineRatioKeys.reduce((sum, k) => sum + Number(offline[k] || 0), 0);
  const offlineValid = Math.abs(offlineTotal - 1) < 0.0001;

  // UID校验：有比例但没UID
  const opsUidValid = !(Number(offline.companyOpsEquityRatio) > 0 && !offline.companyOpsUid);
  const marketUidValid = !(Number(offline.companyMarketEquityRatio) > 0 && !offline.companyMarketUid);
  const schoolUidValid = !(Number(offline.companySchoolEquityRatio) > 0 && !offline.companySchoolUid);

  // 线上留存比例校验
  const onlineRetentionValid = Number(online.poolRetentionRatio) >= 0 && Number(online.poolRetentionRatio) <= 1;

  const allValid = offlineValid && opsUidValid && marketUidValid && schoolUidValid && onlineRetentionValid;

  async function handleSave() {
    if (!offlineValid) {
      alert(`线下支付分红比例之和必须为100%，当前为${(offlineTotal * 100).toFixed(2)}%`);
      return;
    }
    if (!opsUidValid) {
      alert("公司运营账号权益金比例大于0时，必须填写运营账号UID");
      return;
    }
    if (!marketUidValid) {
      alert("公司市场账号权益金比例大于0时，必须填写市场账号UID");
      return;
    }
    if (!schoolUidValid) {
      alert("公司商学院账号权益金比例大于0时，必须填写商学院账号UID");
      return;
    }
    if (!onlineRetentionValid) {
      alert("线上入池资金公司留存比例必须在0%~100%之间");
      return;
    }

    setLoading(true);
    try {
      const { msg } = await asyncRequest({
        method: "POST",
        path: "/system/config/payment-config/save",
        data: { offline, online },
      });
      alert(msg, "success");
    } catch (e) {
      console.error("保存支付分红配置失败:", e);
    } finally {
      setLoading(false);
    }
  }

  function handleOfflineChange(key, value) {
    setOffline({ ...offline, [key]: value });
  }

  function handleOnlineChange(key, value) {
    setOnline({ ...online, [key]: value });
  }

  function renderRatioInput(label, key, value, width = 120) {
    return (
      <FormItem labelStyle={{ width: 220 }} title={label} required>
        <TextField
          type="number"
          autoComplete="off"
          size="small"
          value={value}
          onChange={(e) => handleOfflineChange(key, e.target.value)}
          inputProps={{ step: 0.01, min: 0, max: 1 }}
          sx={{ width }}
        />
        <Typography component="span" ml={1} fontSize={14} color="text.secondary">
          （{(Number(value) * 100).toFixed(0)}%）
        </Typography>
      </FormItem>
    );
  }

  return (
    <Page title="支付业务配置">
      <Container maxWidth="md">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            支付业务配置
          </Typography>
        </Stack>

        {/* 线下支付分红配置 */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            线下支付分红配置
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 2 }}>
            商户让利额按比例分配，分红权 = 额度金额 x 50% / 拼团贡献值单价
          </Typography>

          {/* 分红权部分（入池+给拼团贡献值） */}
          <Typography variant="subtitle2" sx={{ mb: 1, ml: 1 }} color="primary">
            分红权（入池 + 给拼团贡献值）
          </Typography>
          {renderRatioInput("商家分红权比例", "merchantDividendRatio", offline.merchantDividendRatio)}
          {renderRatioInput("消费者分红权比例", "consumerDividendRatio", offline.consumerDividendRatio)}
          {renderRatioInput("消费者推荐人分红权比例", "consumerReferrerDividendRatio", offline.consumerReferrerDividendRatio)}
          <Alert severity="info" sx={{ ml: 29, mb: 1, maxWidth: 500 }}>
            消费者推荐人：若推荐人是店铺则发给该店铺管理员
          </Alert>
          {renderRatioInput("店铺推荐人分红权比例", "merchantReferrerDividendRatio", offline.merchantReferrerDividendRatio)}

          <Divider sx={{ my: 2 }} />

          {/* 非入池部分 */}
          <Typography variant="subtitle2" sx={{ mb: 1, ml: 1 }} color="warning.main">
            非入池部分（不入池）
          </Typography>
          {renderRatioInput("公司留存比例", "companyRetentionRatio", offline.companyRetentionRatio)}
          <Alert severity="info" sx={{ ml: 29, mb: 1, maxWidth: 500 }}>
            公司留存：不入池，不给权益金，平台直接留存
          </Alert>
          {renderRatioInput("其他角色股东权益金比例", "otherRolesEquityRatio", offline.otherRolesEquityRatio)}
          <Alert severity="info" sx={{ ml: 29, mb: 1, maxWidth: 500 }}>
            其他角色股东权益金：不入池，按系统权益金分配规则发放给股东/代理等
          </Alert>

          <Divider sx={{ my: 2 }} />

          {/* 公司账号权益金 */}
          <Typography variant="subtitle2" sx={{ mb: 1, ml: 1 }} color="secondary">
            公司账号权益金（不入池，以权益金发放到指定UID）
          </Typography>

          {renderRatioInput("公司运营账号权益金比例", "companyOpsEquityRatio", offline.companyOpsEquityRatio)}
          <FormItem labelStyle={{ width: 220 }} title="公司运营账号UID" required={Number(offline.companyOpsEquityRatio) > 0}>
            <TextField
              autoComplete="off"
              size="small"
              value={offline.companyOpsUid}
              onChange={(e) => handleOfflineChange("companyOpsUid", e.target.value)}
              placeholder="请输入UID"
              sx={{ width: 200 }}
            />
          </FormItem>

          {renderRatioInput("公司市场账号权益金比例", "companyMarketEquityRatio", offline.companyMarketEquityRatio)}
          <FormItem labelStyle={{ width: 220 }} title="公司市场账号UID" required={Number(offline.companyMarketEquityRatio) > 0}>
            <TextField
              autoComplete="off"
              size="small"
              value={offline.companyMarketUid}
              onChange={(e) => handleOfflineChange("companyMarketUid", e.target.value)}
              placeholder="请输入UID"
              sx={{ width: 200 }}
            />
          </FormItem>

          {renderRatioInput("公司商学院账号权益金比例", "companySchoolEquityRatio", offline.companySchoolEquityRatio)}
          <FormItem labelStyle={{ width: 220 }} title="公司商学院账号UID" required={Number(offline.companySchoolEquityRatio) > 0}>
            <TextField
              autoComplete="off"
              size="small"
              value={offline.companySchoolUid}
              onChange={(e) => handleOfflineChange("companySchoolUid", e.target.value)}
              placeholder="请输入UID"
              sx={{ width: 200 }}
            />
          </FormItem>

          {/* 比例总和校验 */}
          {!offlineValid && (
            <Alert severity="error" sx={{ ml: 29, mb: 1 }}>
              所有比例之和必须为100%，当前为 {(offlineTotal * 100).toFixed(2)}%
            </Alert>
          )}
          {!opsUidValid && (
            <Alert severity="error" sx={{ ml: 29, mb: 1 }}>
              公司运营账号权益金比例大于0时，必须填写运营账号UID
            </Alert>
          )}
          {!marketUidValid && (
            <Alert severity="error" sx={{ ml: 29, mb: 1 }}>
              公司市场账号权益金比例大于0时，必须填写市场账号UID
            </Alert>
          )}
          {!schoolUidValid && (
            <Alert severity="error" sx={{ ml: 29, mb: 1 }}>
              公司商学院账号权益金比例大于0时，必须填写商学院账号UID
            </Alert>
          )}
          {offlineValid && (
            <Alert severity="success" sx={{ ml: 29, mb: 1 }}>
              比例总和校验通过：{(offlineTotal * 100).toFixed(0)}%
            </Alert>
          )}
        </Paper>

        {/* 线上支付配置 */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            线上支付配置（秒杀区/金银通专区）
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <FormItem labelStyle={{ width: 220 }} title="入池资金公司留存比例" required>
            <TextField
              type="number"
              autoComplete="off"
              size="small"
              value={online.poolRetentionRatio}
              onChange={(e) => handleOnlineChange("poolRetentionRatio", e.target.value)}
              inputProps={{ step: 0.01, min: 0, max: 1 }}
              sx={{ width: 120 }}
            />
            <Typography component="span" ml={1} fontSize={14} color="text.secondary">
              （{(Number(online.poolRetentionRatio) * 100).toFixed(0)}%）
            </Typography>
          </FormItem>

          {!onlineRetentionValid && (
            <Alert severity="error" sx={{ ml: 29, mb: 1 }}>
              入池资金公司留存比例必须在0%~100%之间
            </Alert>
          )}

          <Alert severity="info" sx={{ ml: 29, mt: 1 }}>
            设置后：实际入池金额 = 原入池金额 x (1 - 留存比例)，用户分红权不变。
            <br />
            例如：留存比例20%，原入池100元 → 实际入池80元，用户仍获得100x50%/贡献值单价的贡献值。
          </Alert>
        </Paper>

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={2}>
          <Button variant="outlined" onClick={fetchConfig}>
            重置
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={loading || !allValid}>
            保存配置
          </Button>
        </Stack>
      </Container>
    </Page>
  );
}
